"use client";

import * as React from "react";
import { Link, useRouter } from "@tanstack/react-router";
import {
  IconDotsVertical,
  IconEdit,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DeleteAgencyDialog } from "@/components/delete-agency-dialog";
import {
  AddAgencyDialog,
  EditAgencyDialog,
} from "@/features/agency/components";
import { useDeleteAgency } from "@/hooks/use-agencies";
import { isApiError } from "@/lib/axios";

// Agency schema
export const agencySchema = z.object({
  id: z.string(),
  name: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type Agency = z.infer<typeof agencySchema>;

const DELETE_FALLBACK = "Failed to delete agency";

/**
 * Compose operator-readable copy for a delete that failed.
 *
 * There is DELIBERATELY no branch on `error.code` here. The endpoint used to
 * refuse with `AGENCY_HAS_MEMBERS` or `AGENCY_HAS_CONTENT_TRIPS`; it no longer
 * raises either, and the delete now cascades regardless of what the agency
 * holds. Those branches were removed rather than left in place, because an
 * unreachable one reads as a live rule to the next person in this file.
 *
 * What is left is a genuine outcome: 404 for an agency someone else already
 * removed, and the server's own sentence for anything else.
 */
function resolveDeleteErrorMessage(error: unknown, agencyName: string): string {
  if (isApiError(error)) {
    if (error.statusCode === 404) {
      return `Can't delete "${agencyName}" — it no longer exists. Refresh the page.`;
    }
    /**
     * Narrowed, not just optional-chained. `message` is TYPED `string |
     * undefined`, but the wire is a Nest error body, which sends an ARRAY for
     * validation errors — and `.trim()` on an array throws, inside the handler
     * whose whole job is to explain a failure. The venue-app equivalent
     * (`agencyApiError`) guards it the same way.
     */
    const detail =
      typeof error.data?.message === "string" ? error.data.message.trim() : "";
    return detail || DELETE_FALLBACK;
  }
  if (error instanceof Error && error.message) return error.message;
  return DELETE_FALLBACK;
}

interface AgenciesTableProps {
  data: Agency[];
  isLoading?: boolean;
  error?: string | null;
  totalCount?: number;
  pageIndex: number;
  pageSize: number;
  searchQuery: string;
  onPageChange: (pageIndex: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onSearchChange: (search: string) => void;
}

export function AgenciesTable({
  data,
  isLoading = false,
  error = null,
  totalCount = 0,
  pageIndex,
  pageSize,
  searchQuery,
  onPageChange,
  onPageSizeChange: _onPageSizeChange,
  onSearchChange,
}: AgenciesTableProps) {
  const router = useRouter();
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});
  const [editingAgency, setEditingAgency] = React.useState<Agency | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = React.useState(false);
  const [deletingAgency, setDeletingAgency] = React.useState<Agency | null>(
    null,
  );
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);

  const deleteMutation = useDeleteAgency();

  const handleEditClick = React.useCallback((agency: Agency) => {
    setEditingAgency(agency);
    setIsEditDialogOpen(true);
  }, []);

  const handleDeleteClick = React.useCallback((agency: Agency) => {
    setDeletingAgency(agency);
    setIsDeleteDialogOpen(true);
  }, []);

  const handleDeleteConfirm = () => {
    if (!deletingAgency) return;

    deleteMutation.mutate(deletingAgency.id, {
      onSuccess: () => {
        toast.success(`Agency "${deletingAgency.name}" deleted successfully!`);
        setIsDeleteDialogOpen(false);
        setDeletingAgency(null);
      },
      onError: (error) => {
        // The confirm button closes the dialog on click, so a failure is
        // reported in the toast rather than inside the dialog.
        toast.error(resolveDeleteErrorMessage(error, deletingAgency.name));
      },
    });
  };

  // Column definitions
  const columns: ColumnDef<Agency>[] = React.useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Agency Name",
        // An anchor rather than a div: the row's onClick is mouse-only, so
        // without a real link there is no keyboard route to agency detail at
        // all. Preflight makes `a` inherit colour and decoration, so this
        // renders identically to the div it replaced.
        cell: ({ row }) => (
          <Link
            to="/agencies/$agencyId"
            params={{ agencyId: row.original.id }}
            onClick={(event) => event.stopPropagation()}
            className="block font-medium truncate max-w-xs rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            title={row.getValue("name") as string}
          >
            {row.getValue("name")}
          </Link>
        ),
      },
      {
        accessorKey: "createdAt",
        header: "Created At",
        cell: ({ row }) => {
          const date = new Date(row.getValue("createdAt"));
          return (
            <div className="text-muted-foreground">
              {date.toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </div>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => {
          const agency = row.original;

          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="data-[state=open]:bg-muted size-8"
                  size="icon"
                  onClick={(e) => e.stopPropagation()}
                >
                  <IconDotsVertical className="size-4" />
                  <span className="sr-only">Open menu</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditClick(agency);
                  }}
                >
                  <IconEdit className="mr-2 size-4" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  variant="destructive"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(agency);
                  }}
                  disabled={deleteMutation.isPending}
                >
                  <IconTrash className="mr-2 size-4" />
                  {deleteMutation.isPending ? "Deleting..." : "Delete"}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [deleteMutation.isPending, handleDeleteClick, handleEditClick],
  );

  const table = useReactTable({
    data,
    columns,
    pageCount: Math.ceil(totalCount / pageSize),
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination: {
        pageIndex,
        pageSize,
      },
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    manualPagination: true,
  });

  const totalPages = Math.ceil(totalCount / pageSize);
  const canPreviousPage = pageIndex > 0;
  const canNextPage = pageIndex < totalPages - 1;

  return (
    <div className="w-full space-y-4">
      {/* Header with search and add button */}
      <div className="flex items-center justify-between">
        <Input
          aria-label="Search agencies"
          placeholder="Search agencies..."
          value={searchQuery}
          onChange={(event) => onSearchChange(event.target.value)}
          className="max-w-sm"
          disabled={isLoading}
        />
        <AddAgencyDialog>
          <Button>
            <IconPlus className="mr-2 size-4" />
            Add Agency
          </Button>
        </AddAgencyDialog>
      </div>

      {/* Edit Agency Dialog */}
      {editingAgency && (
        <EditAgencyDialog
          agency={editingAgency}
          open={isEditDialogOpen}
          onOpenChange={setIsEditDialogOpen}
        />
      )}

      {/* Delete Agency Dialog */}
      <DeleteAgencyDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirm={handleDeleteConfirm}
        agencyName={deletingAgency?.name}
        isDeleting={deleteMutation.isPending}
      />

      {/* Error State */}
      {error && (
        <div
          role="alert"
          className="rounded-lg border border-destructive/50 bg-destructive/10 p-4"
        >
          <p className="text-sm text-destructive">
            Error loading agencies: {error}
          </p>
        </div>
      )}

      {/* Table */}
      <div className="overflow-hidden rounded-lg border">
        <Table>
          <TableHeader className="bg-muted">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  <div
                    role="status"
                    className="flex items-center justify-center"
                  >
                    <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                    <span className="ml-2 text-muted-foreground">
                      Loading agencies...
                    </span>
                  </div>
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() =>
                    router.navigate({
                      to: `/agencies/$agencyId`,
                      params: { agencyId: row.original.id },
                    })
                  }
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  <div className="text-muted-foreground">
                    {searchQuery
                      ? "No agencies found matching your search."
                      : "No agencies found. Add one to get started."}
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between px-2">
        <div className="text-muted-foreground flex-1 text-sm">
          Showing {data.length > 0 ? pageIndex * pageSize + 1 : 0} to{" "}
          {Math.min((pageIndex + 1) * pageSize, totalCount)} of {totalCount}{" "}
          agencies
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(pageIndex - 1)}
            disabled={!canPreviousPage || isLoading}
          >
            Previous
          </Button>
          <div className="text-sm font-medium">
            Page {totalPages > 0 ? pageIndex + 1 : 0} of {totalPages}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(pageIndex + 1)}
            disabled={!canNextPage || isLoading}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
